/**
 * Global Crash & JS Error Reporter
 * Captures unhandled exceptions, promise rejections, and React component crashes.
 */

import { telemetryService } from './telemetryService';

export function initCrashReporter(): void {
  if (typeof window === 'undefined') return;

  // 1. Unhandled JavaScript runtime errors
  window.addEventListener('error', (event: ErrorEvent) => {
    // Ignore errors originating from browser extensions, user scripts, or anonymous VM contexts
    const filename = event.filename || '';
    if (
      filename.includes('chrome-extension://') ||
      filename.includes('moz-extension://') ||
      filename.includes('safari-extension://') ||
      filename.startsWith('VM') ||
      !filename
    ) {
      return;
    }

    telemetryService.recordError({
      message: event.message || 'Uncaught Error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      fatal: true,
    });
  });

  // 2. Unhandled Promise Rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;

    telemetryService.recordError({
      message: `Unhandled Promise Rejection: ${message}`,
      stack,
      fatal: false,
    });
  });
}

/**
 * Hook for React ErrorBoundary integration
 */
export function reportReactCrash(error: Error, errorInfo?: { componentStack?: string | null }): void {
  telemetryService.recordCrash(error, {
    fatal: true,
    componentStack: errorInfo?.componentStack || undefined,
  });
}
